import { useContext, useMemo } from 'react';

import BankStatementsOrganizerCard from '@/components/BankStatementsOrganizerCard';
import BookingAttachmentCard from '@/components/BookingAttachmentCard';
import CreditReportCard from '@/components/CreditReportCard';
import CustomTermsCard from '@/components/CustomTermsCard';
import FormRequestCard from '@/components/FormRequestCard';
import InvoiceCard from '@/components/InvoiceCard';
import RenderFile from '@/components/Message/RenderFile';
import PaymentLinkButton from '@/components/PaymentLinkButton';
import UserDocumentRequestCard from '@/components/UserDocumentRequestCard';
import { IDENTIFIERS, getNoodleFrontendUrl } from '@/helpers/urlsHelper';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import TeamsContext from '@/providers/Teams/TeamsContext';
import { DocumentRequestStatus } from '@/tsClient/servers/noodleDocuments/document-api-models';
import {
  FormStatus,
  INVOICE_STATUS,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES,
  type MessageAttachment,
  type MessageAttachmentWithTypedData,
} from '@/typings/api-models';

import ESignatureRequestCard from '../ESignatureRequestCard';

/** Renders a document attachment using the existing RenderFile component, linking to the document page. */
const DocumentAttachmentRenderFile = ({
  creatorSlug,
  documentId,
  fileName,
  mimeType,
}: {
  creatorSlug: string;
  documentId: string;
  fileName: string;
  mimeType: string;
}): React.ReactElement => {
  const file = useMemo(
    () => ({
      id: documentId,
      name: fileName,
      type: mimeType,
      url: getNoodleFrontendUrl(IDENTIFIERS.DOCUMENT_BY_ID, { creatorSlug, documentId }),
    }),
    [creatorSlug, documentId, fileName, mimeType],
  );

  return <RenderFile file={file} />;
};

type Props = {
  creator: Parameters<typeof FormRequestCard>[0]['creator'];
  attachment: Pick<MessageAttachment, 'id' | 'referenceId' | 'canAccess' | 'completedAt' | 'integrationSlugs'> &
    MessageAttachmentWithTypedData & {
      assigneeIds: string[];
    };
  isWorkflowAttachment?: boolean;
  userWorkflowId?: string;
  reload?: () => Promise<void>;
  setIsFulfilled?: (f: boolean) => void;
  setIsFailed?: (f: boolean) => void;
  setIsSkipped?: (f: boolean) => void;
  setInReview?: (f: boolean) => void;
  isInChat?: boolean;
  overrideIsAssigned?: (f: boolean) => void;
};

const CommentAttachment: React.FC<Props> = ({
  attachment,
  creator,
  isWorkflowAttachment,
  userWorkflowId,
  reload,
  setIsFulfilled,
  setIsFailed,
  setIsSkipped,
  setInReview,
  isInChat,
  overrideIsAssigned,
}) => {
  const { isInDashboard } = useContext(DashboardContext);
  const { currentTeamOwner } = useContext(TeamsContext);

  const canShowBankIntegrationFeature = useFeatureFlag('bank-statements-organizer', { creatorId: creator.slug });

  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER) {
    const { data } = attachment;
    return (
      <UserDocumentRequestCard
        setIsFulfilled={setIsFulfilled}
        setInReview={setInReview}
        setIsSkipped={setIsSkipped}
        creatorSlug={creator.slug}
        canClick={!isWorkflowAttachment || attachment.canAccess}
        canSkip={
          creator.slug === currentTeamOwner?.slug &&
          !isInChat &&
          data.status !== DocumentRequestStatus.SUCCEEDED &&
          data.status !== DocumentRequestStatus.SKIPPED
        }
        request={attachment.data}
        completedAt={attachment.completedAt}
        reload={reload}
      />
    );
  }
  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_LINK) {
    return (
      <PaymentLinkButton
        setIsFulfilled={setIsFulfilled}
        canClick={!isWorkflowAttachment || attachment.canAccess}
        data={attachment.data}
        completedAt={attachment.completedAt}
      />
    );
  }
  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST) {
    const { data } = attachment;
    return (
      <FormRequestCard
        setIsFulfilled={setIsFulfilled}
        setIsSkipped={setIsSkipped}
        onChange={reload}
        canClick={!isWorkflowAttachment || attachment.canAccess}
        canSkip={creator.slug === currentTeamOwner?.slug && !isInChat && data.status !== FormStatus.COMPLETED}
        creator={creator}
        request={{
          ...data,
          person: { id: data.personId },
        }}
        completedAt={attachment.completedAt}
        integrationSlugs={attachment.integrationSlugs}
      />
    );
  }
  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT) {
    const { data } = attachment;
    return (
      <CreditReportCard
        setIsFulfilled={setIsFulfilled}
        setIsSkipped={setIsSkipped}
        onChange={reload}
        userWorkflowId={userWorkflowId}
        canClick={attachment.canAccess}
        canSkip={creator.slug === currentTeamOwner?.slug && !isInChat && !data.completedAt}
        creator={creator}
        request={data}
        assigneeIds={attachment.assigneeIds}
      />
    );
  }
  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE) {
    return (
      <InvoiceCard
        setIsFulfilled={setIsFulfilled}
        setIsFailed={setIsFailed}
        setIsSkipped={setIsSkipped}
        canClick={attachment.canAccess}
        canSkip={
          creator.slug === currentTeamOwner?.slug && !isInChat && attachment.data.invoice.status !== INVOICE_STATUS.PAID
        }
        invoice={attachment.data.invoice}
        creatorSlug={creator.slug}
        reload={reload}
      />
    );
  }
  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS) {
    return (
      <CustomTermsCard
        canClick={attachment.canAccess}
        canSkip={creator.slug === currentTeamOwner?.slug && !isInChat && !attachment.data.customTerms.agreedAt}
        setIsFulfilled={setIsFulfilled}
        setIsSkipped={setIsSkipped}
        onChange={reload}
        customTerms={attachment.data.customTerms}
        template={attachment.data.template}
        isInDashboard={isInDashboard}
      />
    );
  }
  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING) {
    return (
      <BookingAttachmentCard
        booking={attachment.data.booking}
        canSkip={
          creator.slug === currentTeamOwner?.slug &&
          !isInChat &&
          (!attachment.data.booking.startAt || new Date() < new Date(attachment.data.booking.startAt))
        }
        canClick={attachment.canAccess}
        reload={reload}
        setIsFulfilled={setIsFulfilled}
        overrideIsAssigned={overrideIsAssigned}
        isInDashboard={isInDashboard}
      />
    );
  }
  if (
    attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.BANK_ACCOUNT_INTEGRATION &&
    canShowBankIntegrationFeature
  ) {
    return (
      <BankStatementsOrganizerCard
        creator={creator}
        userWorkflowId={userWorkflowId}
        canClick={attachment.canAccess}
        canSkip={creator.slug === currentTeamOwner?.slug && !isInChat}
        setIsSkipped={setIsSkipped}
        request={{ bankAccountIntegrationId: attachment.data.id }}
        status={attachment.data.status}
        reload={reload}
        setIsFulfilled={setIsFulfilled}
      />
    );
  }
  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.E_SIGNATURE_REQUEST) {
    return (
      <ESignatureRequestCard
        creator={creator}
        request={attachment.data}
        canClick={attachment.canAccess}
        canSkip={creator.slug === currentTeamOwner?.slug && !isInChat}
        setIsSkipped={setIsSkipped}
        setIsFulfilled={setIsFulfilled}
        reload={reload}
        status={attachment.data.status}
      />
    );
  }
  if (attachment.referenceType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT) {
    return (
      <DocumentAttachmentRenderFile
        creatorSlug={creator.slug}
        documentId={attachment.referenceId}
        fileName={attachment.data.fileName}
        mimeType={attachment.data.mimeType}
      />
    );
  }
  return null;
};

export default CommentAttachment;
